import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Badge
} from '@mui/material';
import { Close, Warning, DeleteOutline, BarChart, InfoOutlined } from '@mui/icons-material';
import { SessionDetailsCube, SessionProps } from './session-details-cube';

// Define interfaces for the component
interface PodStatusChange {
  podName: string;
  previousStatus: string;
  newStatus: string;
  reason: string;
  detailedReason?: string;
  timestamp: number;
  nodeName?: string;
  sessions?: SessionProps[];
}

interface TimeWindowSummary {
  startTime: number;
  endTime: number;
  statusChanges: {
    [status: string]: number;
  };
  totalChanges: number;
  topStatusChanges: Array<{status: string, count: number}>; // Track top status changes for quick access
}

interface K8sAnalyzerPodHistoryPanelProps {
  pods: any[];
  open: boolean;
  onClose: () => void;
  onLogCountChange?: (count: number) => void;
}

// Non-running pod statuses 
const NON_RUNNING_STATUSES = ['Error', 'OOMKilled', 'ContainerStatusUnknown', 'Completed'];

// Common error reasons and explanations
const ERROR_EXPLANATIONS: Record<string, string> = {
  'CrashLoopBackOff': 'Container is repeatedly crashing. Check for application errors in logs.',
  'CreateContainerError': 'Failed to create container. May indicate permission issues or resource constraints.',
  'ImagePullBackOff': 'Failed to pull the container image. Check registry credentials or image availability.',
  'OOMKilled': 'Container was terminated due to Out Of Memory. Consider increasing memory limits.',
  'ContainerStatusUnknown': 'Kubernetes cannot determine container status. May indicate node issues or network problems.',
  'Error': 'General error state. Check pod events and logs for specific details.',
  'Error: Evicted': 'Pod was evicted from the node. Usually due to resource pressure (low memory or disk space).',
  'OutOfmemory': 'Container used more memory than its limit. Increase memory allocation.',
  'NodeNotReady': 'The node hosting this pod is not ready. Check node conditions.',
  'Terminating': 'Pod is being terminated. This is part of normal lifecycle management.',
};

// Maximum number of status changes to keep
const MAX_HISTORY_ITEMS = 10000;

// localStorage keys
const STORAGE_KEYS = {
  POD_STATUS_CHANGES: 'k8s-analyzer-pod-status-changes',
  TIME_WINDOW_SUMMARIES: 'k8s-analyzer-time-window-summaries',
  POD_STATUS_MAP: 'k8s-analyzer-pod-status-map'
};

export function K8sAnalyzerPodHistoryPanel({ pods, open, onClose, onLogCountChange }: K8sAnalyzerPodHistoryPanelProps) {
  // State for pod status changes history
  const [podStatusChanges, setPodStatusChanges] = useState<PodStatusChange[]>([]);
  const [timeWindowSummaries, setTimeWindowSummaries] = useState<TimeWindowSummary[]>([]);
  const [timeWindowMinutes, setTimeWindowMinutes] = useState<number>(5);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [podNameFilter, setPodNameFilter] = useState<string>('');
  const [changesPerMinute, setChangesPerMinute] = useState<number>(0);
  const [statusFrequencies, setStatusFrequencies] = useState<{[key: string]: number}>({});
  const [currentPodStatusCounts, setCurrentPodStatusCounts] = useState<{[key: string]: number}>({});
  const [showTopProblemSessions, setShowTopProblemSessions] = useState<boolean>(true);

  // Track pod statuses for detecting changes
  const [podStatusMap, setPodStatusMap] = useState<{[podName: string]: string}>({});

  // Reference to track if this is the first render
  const isFirstRender = useRef(true);
  
  // Load data from localStorage on component mount
  useEffect(() => {
    loadFromLocalStorage();
  }, []);
  
  // Save pod status changes to localStorage
  const saveToLocalStorage = () => {
    try {
      // Save pod status changes
      localStorage.setItem(STORAGE_KEYS.POD_STATUS_CHANGES, JSON.stringify(podStatusChanges));
      // Save time window summaries
      localStorage.setItem(STORAGE_KEYS.TIME_WINDOW_SUMMARIES, JSON.stringify(timeWindowSummaries));
      // Save pod status map
      localStorage.setItem(STORAGE_KEYS.POD_STATUS_MAP, JSON.stringify(podStatusMap));
    } catch (error) {
      console.error('Error saving pod history to localStorage:', error);
    }
  };
  
  // Load pod status changes from localStorage
  const loadFromLocalStorage = () => {
    try {
      // Load pod status changes
      const savedPodStatusChanges = localStorage.getItem(STORAGE_KEYS.POD_STATUS_CHANGES);
      if (savedPodStatusChanges) {
        const parsedChanges = JSON.parse(savedPodStatusChanges) as PodStatusChange[];
        setPodStatusChanges(parsedChanges);
        
        // Notify parent about the log count
        if (onLogCountChange) {
          onLogCountChange(parsedChanges.length);
        }
      }
      
      // Load time window summaries
      const savedTimeWindowSummaries = localStorage.getItem(STORAGE_KEYS.TIME_WINDOW_SUMMARIES);
      if (savedTimeWindowSummaries) {
        setTimeWindowSummaries(JSON.parse(savedTimeWindowSummaries));
      }
      
      // Load pod status map
      const savedPodStatusMap = localStorage.getItem(STORAGE_KEYS.POD_STATUS_MAP);
      if (savedPodStatusMap) {
        setPodStatusMap(JSON.parse(savedPodStatusMap));
      }
    } catch (error) {
      console.error('Error loading pod history from localStorage:', error);
    }
  };
  
  // Function to clear history
  const handleClearHistory = () => {
    setPodStatusChanges([]);
    setTimeWindowSummaries([]);
    setStatusFrequencies({});
    setPodStatusMap({});
    
    // Clear localStorage entries
    localStorage.removeItem(STORAGE_KEYS.POD_STATUS_CHANGES);
    localStorage.removeItem(STORAGE_KEYS.TIME_WINDOW_SUMMARIES);
    localStorage.removeItem(STORAGE_KEYS.POD_STATUS_MAP);
    
    // Update badge count
    if (onLogCountChange) {
      onLogCountChange(0);
    }
  };

  // Status color mapping function
  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'running':
        return 'var(--bulk-success)'; // green
      case 'completed':
        return '#2196f3'; // blue
      case 'containerstatusunknown':
        return '#9e9e9e'; // grey
      case 'error':
        return 'var(--bulk-error)'; // red
      case 'oomkilled':
        return 'var(--bulk-warning)'; // orange
      case 'removed':
        return '#9c27b0'; // purple
      default:
        return '#9e9e9e'; // grey
    }
  };

  // Create a status chip component for consistent styling
  const StatusChip = ({ status, count }: { status: string, count?: number }) => (
    <Tooltip title={ERROR_EXPLANATIONS[status] || ''}>
      <Chip
        label={count !== undefined ? `${status}: ${count}` : status}
        size="small"
        sx={{
          bgcolor: getStatusColor(status),
          color: 'white',
          fontWeight: 'bold'
        }}
        icon={ERROR_EXPLANATIONS[status] ? <InfoOutlined fontSize="small" /> : undefined}
      />
    </Tooltip>
  );
  
  // Helper function to extract detailed reason from pod object
  const getDetailedReasonFromPod = (pod: any): { reason: string, detailedReason?: string } => {
    if (!pod.containerStatuses || pod.containerStatuses.length === 0) {
      return { reason: 'No container status information' };
    }

    const containerStatus = pod.containerStatuses[0];
    let reason = 'Unknown reason';
    let detailedReason: string | undefined;

    // Extract waiting reason (like CrashLoopBackOff, ImagePullBackOff, etc)
    if (containerStatus.state?.waiting?.reason) {
      reason = containerStatus.state.waiting.reason;
      detailedReason = ERROR_EXPLANATIONS[reason];
    } 
    // Extract terminated reason (like OOMKilled, Error, Completed, etc)
    else if (containerStatus.state?.terminated?.reason) {
      reason = containerStatus.state.terminated.reason;
      detailedReason = ERROR_EXPLANATIONS[reason];
    }
    // For ContainerStatusUnknown, we add specific explanation
    else if (pod.status === 'ContainerStatusUnknown') {
      reason = 'ContainerStatusUnknown';
      detailedReason = ERROR_EXPLANATIONS.ContainerStatusUnknown;
      
      // Add node-related info if available
      if (pod.nodeName) {
        detailedReason += ` Pod was on node: ${pod.nodeName}`;
      }
    }
    // Special case for Error status
    else if (pod.status === 'Error') {
      reason = 'Error';
      
      // Check if we have a more specific reason like Evicted
      if (pod.reason) {
        reason = `Error: ${pod.reason}`;
        detailedReason = ERROR_EXPLANATIONS[reason] || ERROR_EXPLANATIONS.Error;
      } else {
        detailedReason = ERROR_EXPLANATIONS.Error;
      }
    }
    // Default for running pods
    else if (pod.status === 'Running') {
      reason = 'Running';
    }
    
    return { reason, detailedReason };
  };

  // Function to update time window summaries
  const updateTimeWindowSummaries = (newChanges: PodStatusChange[]) => {
    const currentTime = Date.now();
    const windowSizeMs = timeWindowMinutes * 60 * 1000;
    
    // Check if we need to create a new window
    if (timeWindowSummaries.length === 0 || 
        currentTime - timeWindowSummaries[0].startTime >= windowSizeMs) {
      
      // Create a new time window
      const newWindow: TimeWindowSummary = {
        startTime: currentTime,
        endTime: currentTime + windowSizeMs,
        statusChanges: {},
        totalChanges: 0,
        topStatusChanges: []
      };
      
      // Add status changes to the window
      newChanges.forEach(change => {
        newWindow.statusChanges[change.newStatus] = 
          (newWindow.statusChanges[change.newStatus] || 0) + 1;
        newWindow.totalChanges++;
      });
      
      // Calculate top status changes
      newWindow.topStatusChanges = Object.entries(newWindow.statusChanges)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([status, count]) => ({ status, count }));
      
      const updatedSummaries = [newWindow, ...timeWindowSummaries];
      setTimeWindowSummaries(updatedSummaries);
      
      // Save updated summaries to localStorage
      try {
        localStorage.setItem(STORAGE_KEYS.TIME_WINDOW_SUMMARIES, JSON.stringify(updatedSummaries));
      } catch (error) {
        console.error('Error saving time window summaries to localStorage:', error);
      }
    } else {
      // Update the current window
      const updated = [...timeWindowSummaries];
      
      // Add new changes to the first window
      newChanges.forEach(change => {
        updated[0].statusChanges[change.newStatus] = 
          (updated[0].statusChanges[change.newStatus] || 0) + 1;
        updated[0].totalChanges++;
      });
      
      // Recalculate top status changes
      updated[0].topStatusChanges = Object.entries(updated[0].statusChanges)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([status, count]) => ({ status, count }));
      
      setTimeWindowSummaries(updated);
      
      // Save updated summaries to localStorage
      try {
        localStorage.setItem(STORAGE_KEYS.TIME_WINDOW_SUMMARIES, JSON.stringify(updated));
      } catch (error) {
        console.error('Error saving time window summaries to localStorage:', error);
      }
    }
  };

  // Calculate changes per minute
  const calculateChangesPerMinute = () => {
    const currentTime = Date.now();
    const oneMinuteAgo = currentTime - 60 * 1000;
    
    // Count changes in the last minute
    const changesInLastMinute = podStatusChanges.filter(
      change => change.timestamp >= oneMinuteAgo
    ).length;
    
    setChangesPerMinute(changesInLastMinute);
  };

  // Format timestamp to readable format
  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp).toLocaleTimeString();
  };

  // Filter function for pod status changes
  const filteredPodStatusChanges = podStatusChanges
    .filter(change => 
      (statusFilter === '' || change.newStatus === statusFilter) &&
      (podNameFilter === '' || change.podName.toLowerCase().includes(podNameFilter.toLowerCase()))
    );

  // Calculate top 3 most frequent non-running statuses from logs history
  const topNonRunningStatuses = useMemo(() => {
    // Count status occurrences in the history logs
    const statusCounts: {[status: string]: number} = {};
    
    podStatusChanges.forEach(change => {
      if (NON_RUNNING_STATUSES.includes(change.newStatus)) {
        statusCounts[change.newStatus] = (statusCounts[change.newStatus] || 0) + 1;
      }
    });
    
    return Object.entries(statusCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([status, count]) => ({ status, count }));
  }, [podStatusChanges]);
  
  // Calculate top problem sessions from the logs history
  const topProblemSessions = useMemo(() => {
    // Track sessions associated with non-running pod statuses
    const sessionProblems: Record<string, { count: number, session: SessionProps }> = {};
    
    // Count session occurrences in problem logs
    podStatusChanges.forEach(change => {
      if (NON_RUNNING_STATUSES.includes(change.newStatus) && 
          change.sessions && 
          change.sessions.length > 0) {
        
        change.sessions.forEach(session => {
          if (session._id) {
            if (!sessionProblems[session._id]) {
              sessionProblems[session._id] = { count: 1, session };
            } else {
              sessionProblems[session._id].count += 1;
            }
          }
        });
      }
    });
    
    // Sort sessions by problem count and take top 5
    return Object.values(sessionProblems)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5); // Show top 5 problem sessions
  }, [podStatusChanges]);
  
  // Initialize pod status map from initial pods only on first render
  useEffect(() => {
    if (isFirstRender.current && pods && pods.length > 0) {
      const initialPodStatusMap: {[podName: string]: string} = {};
      const initialStatusCounts: {[status: string]: number} = {};
      
      pods.forEach(pod => {
        initialPodStatusMap[pod.name] = pod.status;
        initialStatusCounts[pod.status] = (initialStatusCounts[pod.status] || 0) + 1;
      });
      
      // If we have previously saved data, merge it with new pods
      const savedStatusMap = localStorage.getItem(STORAGE_KEYS.POD_STATUS_MAP);
      if (savedStatusMap) {
        // Only update with new pods, keep history for pods not in current view
        const mergedStatusMap = { ...JSON.parse(savedStatusMap), ...initialPodStatusMap };
        setPodStatusMap(mergedStatusMap);
        
        // Save merged map back to localStorage
        localStorage.setItem(STORAGE_KEYS.POD_STATUS_MAP, JSON.stringify(mergedStatusMap));
      } else {
        setPodStatusMap(initialPodStatusMap);
        localStorage.setItem(STORAGE_KEYS.POD_STATUS_MAP, JSON.stringify(initialPodStatusMap));
      }
      
      setCurrentPodStatusCounts(initialStatusCounts);
      isFirstRender.current = false;
    }
  }, [pods]);
   
  // Effect to detect status changes that runs when pod data updates
  useEffect(() => {
    if (!pods || pods.length === 0) return;
    if (isFirstRender.current) return; // Skip on first render since we're just initializing

    const currentTime = Date.now();
    const newPodStatusMap: {[podName: string]: string} = {};
    const newStatusChanges: PodStatusChange[] = [];
    const currentStatusCounts: {[key: string]: number} = {};
    
    // First, calculate current status counts and build new status map
    pods.forEach(pod => {
      const podName = pod.name;
      const currentStatus = pod.status;
      
      // Add to new status map
      newPodStatusMap[podName] = currentStatus;
      
      // Count current statuses
      currentStatusCounts[currentStatus] = (currentStatusCounts[currentStatus] || 0) + 1;
    });
    
    // Then detect changes by comparing with previous map
    pods.forEach(pod => {
      const podName = pod.name;
      const currentStatus = pod.status;
      
      // Check if this pod existed before and its status has changed
      if (podStatusMap[podName] && podStatusMap[podName] !== currentStatus) {
        // Only track changes to/from non-running status
        if (NON_RUNNING_STATUSES.includes(podStatusMap[podName]) || 
            NON_RUNNING_STATUSES.includes(currentStatus)) {
          
          // Get detailed reason for this status change
          const { reason, detailedReason } = getDetailedReasonFromPod(pod);
          
          // Record status change
          newStatusChanges.push({
            podName,
            previousStatus: podStatusMap[podName],
            newStatus: currentStatus,
            reason,
            detailedReason,
            timestamp: currentTime,
            nodeName: pod.nodeName,
            sessions: pod.sessions || []
          });
        }
      }
    });
    
    // Also check for pods that have disappeared
    Object.entries(podStatusMap).forEach(([podName, status]) => {
      // If a pod was in the previous map but not in the current pods
      if (!newPodStatusMap[podName]) {
        // Only track if it was non-running
        if (NON_RUNNING_STATUSES.includes(status)) {
          newStatusChanges.push({
            podName,
            previousStatus: status,
            newStatus: 'Removed',
            reason: 'Pod no longer exists',
            detailedReason: 'The pod has been removed from the cluster completely.',
            timestamp: currentTime
            // No sessions or node info available for removed pods
          });
        }
      }
    });

    // Update pod status map
    const updatedPodStatusMap = { ...newPodStatusMap };
    setPodStatusMap(updatedPodStatusMap);
    localStorage.setItem(STORAGE_KEYS.POD_STATUS_MAP, JSON.stringify(updatedPodStatusMap));
    
    // Update current status counts (for more accurate Top Problem Statuses)
    setCurrentPodStatusCounts(currentStatusCounts);
    
    // Update history frequencies (but don't include in dependencies to avoid infinite loop)
    const newStatusFrequencies = { ...statusFrequencies };
    Object.entries(currentStatusCounts).forEach(([status, count]) => {
      if (NON_RUNNING_STATUSES.includes(status)) {
        newStatusFrequencies[status] = count;
      }
    });
    setStatusFrequencies(newStatusFrequencies);

    // If we have new status changes, add them to history
    if (newStatusChanges.length > 0) {
      setPodStatusChanges(prev => {
        const updatedChanges = [...newStatusChanges, ...prev];
        // Keep only the most recent MAX_HISTORY_ITEMS
        const limitedChanges = updatedChanges.slice(0, MAX_HISTORY_ITEMS);
        
        // Save to localStorage
        localStorage.setItem(STORAGE_KEYS.POD_STATUS_CHANGES, JSON.stringify(limitedChanges));
        
        // Notify parent about the log count change
        if (onLogCountChange) {
          onLogCountChange(limitedChanges.length);
        }
        
        return limitedChanges;
      });
      
      // Update time window summaries
      updateTimeWindowSummaries(newStatusChanges);
    }
    
    // Always update changes per minute
    calculateChangesPerMinute();
  // Remove statusFrequencies from the dependency array to avoid infinite updates
  }, [pods]);

  // Effect to initialize the log count when the component mounts
  useEffect(() => {
    if (onLogCountChange) {
      onLogCountChange(podStatusChanges.length);
    }
  }, []);
  
  // Effect to update log count when the number of logs changes
  useEffect(() => {
    if (onLogCountChange) {
      onLogCountChange(podStatusChanges.length);
    }
  }, [podStatusChanges.length, onLogCountChange]);

  // Reset history when panel is opened if it was previously closed
  useEffect(() => {
    if (open) {
      calculateChangesPerMinute();
    }
  }, [open]);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 500 } } }}
    >
      <Box sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            <Warning color="warning" sx={{ mr: 1, verticalAlign: 'middle' }} />
            Pod Status History
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1">
            Non-running pods history ({podStatusChanges.length}/{MAX_HISTORY_ITEMS})
          </Typography>
          <Chip 
            label={`${changesPerMinute} changes/min`} 
            color={changesPerMinute > 10 ? "warning" : "default"}
            icon={<BarChart />}
          />
        </Box>

        {/* Top 3 most frequent problem statuses */}
        {topNonRunningStatuses.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Top Problem Statuses (Historical)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {topNonRunningStatuses.map(({ status, count }) => (
                <StatusChip 
                  key={status}
                  status={status}
                  count={count}
                />
              ))}
              <Tooltip title="Hover over status chips to see common causes and solutions">
                <IconButton size="small" color="info">
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
        
        {/* Top problem sessions */}
        {topProblemSessions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">
                Top Problem Sessions
              </Typography>
              <Button 
                size="small" 
                onClick={() => setShowTopProblemSessions(!showTopProblemSessions)}
              >
                {showTopProblemSessions ? 'Hide' : 'Show'}
              </Button>
            </Box>
            
            {showTopProblemSessions && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1, 
                p: 1, 
                border: '1px solid var(--bulk-border)', 
                borderRadius: 1,
                bgcolor: '#f5f5f5',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {topProblemSessions.map(({ session, count }) => (
                  <Box 
                    key={session._id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      width: '100%'
                    }}
                  >
                    <Badge 
                      badgeContent={count} 
                      color="error"
                      sx={{ 
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          height: '16px',
                          minWidth: '16px',
                        }
                      }}
                    >
                      <Box sx={{ flexGrow: 1, width: '100%' }}>
                        <SessionDetailsCube session={session} pods={pods} />
                      </Box>
                    </Badge>
                  </Box>
                ))}
                {topProblemSessions.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No sessions with recurring problems detected yet.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Filter by Status"
            >
              <MenuItem value="">All Statuses</MenuItem>
              {NON_RUNNING_STATUSES.map(status => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
              <MenuItem value="Running">Running</MenuItem>
              <MenuItem value="Removed">Removed</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Pod Name Filter"
            variant="outlined"
            size="small"
            value={podNameFilter}
            onChange={(e) => setPodNameFilter(e.target.value)}
            sx={{ flexGrow: 1 }}
          />
        </Box>

        <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel>Time Window</InputLabel>
            <Select
              value={timeWindowMinutes}
              onChange={(e) => setTimeWindowMinutes(Number(e.target.value))}
              label="Time Window"
            >
              <MenuItem value={1}>1 Minute</MenuItem>
              <MenuItem value={5}>5 Minutes</MenuItem>
              <MenuItem value={10}>10 Minutes</MenuItem>
              <MenuItem value={15}>15 Minutes</MenuItem>
              <MenuItem value={30}>30 Minutes</MenuItem>
            </Select>
          </FormControl>
          
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={<DeleteOutline />}
            onClick={handleClearHistory}
          >
            Clear History
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Status Change History */}
        <Typography variant="subtitle2" gutterBottom>
          Pod Status Changes
        </Typography>
        
        {filteredPodStatusChanges.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No status changes recorded yet. Status changes will appear here as they occur.
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
            {filteredPodStatusChanges.map((change, index) => {
              // Calculate if we need to insert a time window summary before this log entry
              const showTimeWindowSummary = timeWindowSummaries.some(window => {
                // Display a time window summary if this log entry is the first one after the window's start time
                return change.timestamp <= window.endTime && 
                      (index === 0 || filteredPodStatusChanges[index - 1].timestamp > window.endTime);
              });
              
              // Find the matching time window if needed
              const matchingWindow = showTimeWindowSummary ? 
                timeWindowSummaries.find(window => 
                  change.timestamp <= window.endTime && 
                  (index === 0 || filteredPodStatusChanges[index - 1].timestamp > window.endTime)
                ) : null;
              
              return (
                <React.Fragment key={index}>
                  {/* Insert time window summary if applicable */}
                  {matchingWindow && (
                    <Box sx={{ my: 2, px: 1 }}>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 1, 
                          bgcolor: '#f5f5f5', 
                          border: '1px dashed #ccc',
                          borderRadius: 1 
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Time Window: {formatTimestamp(matchingWindow.startTime)} - {formatTimestamp(matchingWindow.endTime)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {Object.entries(matchingWindow.statusChanges).map(([status, count]) => (
                            <Chip 
                              key={status}
                              label={`${status}: ${count}`}
                              size="small"
                              sx={{
                                bgcolor: getStatusColor(status),
                                color: 'white'
                              }}
                              onClick={() => setStatusFilter(status)}
                            />
                          ))}
                          <Chip 
                            label={`Total: ${matchingWindow.totalChanges}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Paper>
                    </Box>
                  )}
                  
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {change.podName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(change.timestamp)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" component="span">
                              From:
                            </Typography>
                            <StatusChip status={change.previousStatus} />
                            <Typography variant="caption" color="text.secondary" component="span">
                              To:
                            </Typography>
                            <StatusChip status={change.newStatus} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                            Reason: {change.reason || 'Unknown'}
                          </Typography>
                          {change.nodeName && (
                            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, display: 'block' }}>
                              Node: {change.nodeName}
                            </Typography>
                          )}
                          {change.detailedReason && (
                            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                              {change.detailedReason}
                            </Typography>
                          )}
                          
                          {/* Display sessions if available */}
                          {change.sessions && change.sessions.length > 0 && (
                            <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid var(--bulk-border)' }}>
                              <Typography variant="caption" color="text.secondary" component="div" sx={{ display: 'block', mb: 0.5 }}>
                                Associated Sessions:
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: '120px', overflowY: 'auto' }}>
                                {change.sessions.map((session) => (
                                  <SessionDetailsCube key={session._id} session={session} pods={pods} />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
    </Drawer>
  );
}

export default K8sAnalyzerPodHistoryPanel; 